package com.swetdot.swetgram.data.media

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.protocol.dto.CompleteUploadRequest
import com.swetdot.swetgram.protocol.dto.MediaObject
import com.swetdot.swetgram.protocol.dto.PresignUploadRequest
import com.swetdot.swetgram.protocol.dto.PresignUploadResponse
import com.swetdot.swetgram.protocol.dto.UploadLimits
import io.ktor.client.HttpClient
import io.ktor.client.plugins.onUpload
import io.ktor.client.request.header
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.serialization.SerializationException

/**
 * A stored image, as the rest of the client sees it.
 *
 * `:protocol`'s `MediaObject` stops at this boundary (IMPLEMENTATION_NOTES §10). The difference
 * that matters is `purpose`: the wire enum carries an `UNKNOWN` member so an older build can
 * read a value a later server introduces, and nothing above the data layer has anything useful
 * to do with that. What a screen needs is the id to attach and the URL to draw.
 */
data class UploadedMedia(
    val id: String,
    val url: String,
    val mimeType: String,
    val sizeBytes: Long,
    val width: Int,
    val height: Int,
)

/**
 * The three-step image upload: `createPresignedUpload`, a PUT straight to object storage, then
 * `completeUpload`.
 *
 * THE MIDDLE STEP DOES NOT GO THROUGH OUR API. `upload_url` points at R2, and it is authorised
 * by the signature already baked into that URL. Sending it on the configured client would
 * attach `Authorization: Bearer <access token>` and the three `X-Client-*` headers to a request
 * addressed to a third party -- which hands a live session token to an origin that has no
 * business holding one, and is the kind of leak that is invisible until someone reads a proxy
 * log. It would also fail: `createHttpClient` sets a 15-second socket timeout, which a 10MB
 * upload on a train will exceed, and its `defaultRequest` rewrites the URL against `/api/v1`.
 *
 * So [bareHttp] is a SEPARATE client. Pass `AppContainer.engine` -- the raw engine client -- and
 * never the value `createHttpClient` returns.
 *
 * Local pre-flight failures throw [IllegalArgumentException] rather than reaching the server,
 * following [ProfileRepository][com.swetdot.swetgram.data.profile.ProfileRepository]: the
 * contract's `size_bytes` maximum and `mime_type` enum make those a guaranteed 400, so the
 * round trip buys nothing. Server-side rejections still arrive as [ApiError.Validation] with
 * `media_too_large` or `invalid_media_type`, which is what the UI localizes from.
 */
class UploadRepository(
    private val api: ApiClient,
    bareHttp: HttpClient,
) {
    /**
     * The upload client, pinned to `expectSuccess = false`.
     *
     * `config` shares the engine and costs nothing at rest. It is here so the retry rule below
     * reads the storage provider's status code itself rather than depending on how whoever wired
     * [bareHttp] happened to configure it: with `expectSuccess = true` a 403 arrives as a thrown
     * `ClientRequestException`, which would be indistinguishable from a dropped connection and
     * would get an expired signature retried three times instead of once.
     */
    private val storage: HttpClient = bareHttp.config { expectSuccess = false }

    companion object {
        /**
         * DECISIONS §11: "Mutating requests retry only when explicitly idempotent."
         *
         * A presigned PUT is exactly that. The URL names one object key, the signature pins it,
         * and the body is a fixed byte array -- repeating it overwrites the same bytes with the
         * same bytes. That is a different thing from retrying `sendMessage`, and it is why this
         * may retry where [ApiClient] would not.
         */
        const val MAX_PUT_ATTEMPTS = 3

        /** The same short ladder [ApiClient] uses: 400ms, then 800ms. */
        const val BACKOFF_BASE_MS = 400L

        /**
         * How full the bar gets when the BYTES are all sent.
         *
         * Not 1f, and that is the point. The PUT is followed by `POST /uploads/complete`, and in
         * the chat composer by `sendMessage` after that -- so a bar that filled here reported the
         * work as finished with two round trips still outstanding, and could then produce an
         * error under a progress indicator sitting at 100%. SDS §25's loading state has to be
         * about the work, not about one leg of it.
         *
         * The band is deliberately wide (0.9) rather than proportional: the bytes ARE the part
         * that takes time -- the calls either side are a few hundred bytes each -- so the head of
         * the bar should still track them almost one for one. The remaining tenth is the caller's
         * to spend; this class never reports 1f, because it is never the last thing to run.
         */
        const val PUT_PROGRESS_CEILING = 0.9f
    }

    /**
     * Uploads [image] and returns the stored object.
     *
     * [onProgress] reports `0f..`[PUT_PROGRESS_CEILING] for the PUT only. The two API calls around
     * it are a few hundred bytes each, so a bar that included them would sit at 0 and then jump --
     * worse than one that simply describes the part that takes time.
     *
     * IT NEVER REACHES 1f, on purpose. See [PUT_PROGRESS_CEILING]: `complete` still has to run
     * after the last byte, and the caller usually has a send after that, so filling the bar here
     * would announce a finish the server has not confirmed. The caller owns the last tenth.
     */
    suspend fun upload(
        image: PickedImage,
        purpose: UploadPurpose,
        onProgress: ((Float) -> Unit)? = null,
    ): UploadedMedia {
        return uploadInternal(image, purpose, voice = null, onProgress = onProgress)
    }

    suspend fun uploadVoice(
        voice: RecordedVoice,
        onProgress: ((Float) -> Unit)? = null,
    ): UploadedMedia {
        require(voice.durationMs in UploadLimits.VOICE_MIN_DURATION_MS..UploadLimits.VOICE_MAX_DURATION_MS)
        require(voice.sizeBytes in 1..UploadLimits.VOICE_MAX_SIZE_BYTES)
        require(voice.waveform.isNotEmpty() && voice.waveform.size <= UploadLimits.VOICE_WAVEFORM_SAMPLES)
        require(voice.waveform.all { it in 0..UploadLimits.VOICE_WAVEFORM_MAX })
        val payload = PickedImage(voice.bytes, UploadLimits.VOICE_MIME_TYPE, width = 1, height = 1)
        return uploadInternal(payload, UploadPurpose.CHAT_VOICE, voice, onProgress)
    }

    private suspend fun uploadInternal(
        image: PickedImage,
        purpose: UploadPurpose,
        voice: RecordedVoice?,
        onProgress: ((Float) -> Unit)?,
    ): UploadedMedia {
        require(image.mimeType in UploadLimits.ALLOWED_MIME_TYPES) {
            "`${image.mimeType}` is not one of the contract's upload MIME types"
        }
        require(image.sizeBytes in 1..UploadLimits.MAX_SIZE_BYTES) {
            "An upload must be between 1 and ${UploadLimits.MAX_SIZE_BYTES} bytes, not ${image.sizeBytes}"
        }
        require(image.width > 0 && image.height > 0) {
            "`width` and `height` have `minimum: 1`; the picker produced ${image.width}x${image.height}"
        }

        val maximum = if (purpose == UploadPurpose.CHAT_VOICE) {
            UploadLimits.VOICE_MAX_SIZE_BYTES
        } else {
            UploadLimits.MAX_SIZE_BYTES
        }
        require(image.sizeBytes in 1..maximum)

        val presigned = presign(image, purpose, voice)
        try {
            putToStorage(presigned, image, onProgress)
        } catch (failure: Throwable) {
            // Best effort, and deliberately swallowed: leaving a presign row behind is untidy,
            // but reporting the cleanup's failure instead of the upload's would hide the reason
            // the user's photo did not send. On a CANCELLED scope the discard cannot run at all
            // -- the suspend call fails immediately -- so this tidies real failures only, and
            // the original is rethrown either way.
            runCatching { discard(presigned.uploadId) }
            throw failure
        }
        return complete(presigned.uploadId)
    }

    /**
     * `deleteUpload`. 204.
     *
     * For media that was uploaded and then abandoned -- an avatar picked and the form cancelled.
     * The contract restricts it to an object that is not attached to anything, so a picture
     * already sent in a message answers `409 media_in_use` and stays.
     */
    suspend fun discard(uploadId: String) {
        api.delete<Unit>("uploads/$uploadId")
    }

    // -------------------------------------------------------------------------- the three steps

    /** `createPresignedUpload`. 201. */
    private suspend fun presign(
        image: PickedImage,
        purpose: UploadPurpose,
        voice: RecordedVoice?,
    ): PresignUploadResponse {
        val presigned: PresignUploadResponse = api.post("uploads/presign") {
            setBody(
                PresignUploadRequest(
                    purpose = purpose.wire,
                    filename = uploadFilename(purpose, image.mimeType),
                    mimeType = image.mimeType,
                    sizeBytes = image.sizeBytes,
                    // Optional in the schema, required in practice: after the presign the server
                    // never sees the image, so a row without them can never be completed into a
                    // conformant `MediaObject`. See the note on `PresignUploadRequest`.
                    width = image.width,
                    height = image.height,
                    // `const: true`, and true only because `ImagePicker` re-encodes rather than
                    // forwarding the source bytes. Sending these without doing the work would be
                    // asserting on the user's behalf that their GPS coordinates are gone.
                    exifRemoved = if (voice == null) true else null,
                    compressionApplied = if (voice == null) true else null,
                    durationMs = voice?.durationMs,
                    waveform = voice?.waveform.orEmpty(),
                    codec = voice?.let { UploadLimits.VOICE_CODEC },
                ),
            )
        }

        // A relative or http URL here would be resolved by the upload client against nothing, or
        // would send the user's photo -- and whatever headers came with it -- in the clear. It
        // cannot happen against a correct server, which is exactly why it is worth catching:
        // the failure mode is silent. `SerializationException` because this is the same class of
        // problem as a missing field, which is what kotlinx would already have thrown.
        if (!presigned.uploadUrl.startsWith("https://")) {
            throw SerializationException(
                "`upload_url` must be an absolute https URL, got `${presigned.uploadUrl}`",
            )
        }
        return presigned
    }

    /**
     * The PUT to object storage.
     *
     * Only the headers the presign told us to send are attached. That is not caution about
     * overhead -- the server SIGNS the headers it lists, so omitting one or adding a different
     * value is a signature mismatch, and any header we invent is a header a third party now sees.
     */
    private suspend fun putToStorage(
        presigned: PresignUploadResponse,
        image: PickedImage,
        onProgress: ((Float) -> Unit)?,
    ) {
        var lastError: Throwable? = null
        // A local val so the null check below holds inside the nested progress lambda without
        // relying on a smart cast reaching through two lambda boundaries.
        val report = onProgress

        repeat(MAX_PUT_ATTEMPTS) { attempt ->
            try {
                // Reset per attempt: a retry that resumed the previous attempt's percentage
                // would show a bar that only ever creeps forward while nothing is being sent.
                report?.invoke(0f)

                val response = storage.put(presigned.uploadUrl) {
                    presigned.headers.forEach { (name, value) -> header(name, value) }
                    // Ktor stamps `application/octet-stream` on a ByteArray body when nothing
                    // else says otherwise, and object storage keeps whatever it is told -- so
                    // the CDN would later serve the photo as a download rather than an image.
                    if (presigned.headers.keys.none { it.equals(HttpHeaders.ContentType, ignoreCase = true) }) {
                        header(HttpHeaders.ContentType, image.mimeType)
                    }
                    setBody(image.bytes)
                    if (report != null) {
                        onUpload { sent, total ->
                            val size = total ?: image.sizeBytes
                            if (size > 0) {
                                // Clamped FIRST, then scaled into the band: Ktor can report more
                                // bytes than `sizeBytes` when the engine counts framing, and a
                                // fraction above 1 would push the bar past the ceiling this
                                // whole band exists to hold it under.
                                val fraction = (sent.toDouble() / size.toDouble())
                                    .toFloat()
                                    .coerceIn(0f, 1f)
                                report(fraction * PUT_PROGRESS_CEILING)
                            }
                        }
                    }
                }

                if (response.status.isSuccess()) {
                    // The BYTES are done, which is not the same as the upload being done --
                    // `complete` has not run yet, and in the composer neither has the send. See
                    // [PUT_PROGRESS_CEILING].
                    report?.invoke(PUT_PROGRESS_CEILING)
                    return
                }

                val error = ApiError.Server(
                    status = response.status.value,
                    code = null,
                    // Not the storage provider's XML error body: ERRORS.md owns what a user is
                    // shown, and R2's `<Error><Code>SignatureDoesNotMatch` is not in it.
                    message = "The image store rejected the upload (${response.status.value})",
                )
                // A 403 means the signature is wrong or expired; repeating it changes nothing.
                if (!error.isRetryable || attempt == MAX_PUT_ATTEMPTS - 1) throw error
                lastError = error
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (rejected: ApiError) {
                throw rejected
            } catch (other: Throwable) {
                if (attempt == MAX_PUT_ATTEMPTS - 1) throw ApiError.Offline(other)
                lastError = other
            }

            delay(BACKOFF_BASE_MS shl attempt)
        }

        throw (lastError as? ApiError) ?: ApiError.Offline(lastError)
    }

    /** `completeUpload`. 200, and the only step that produces a media id worth keeping. */
    private suspend fun complete(uploadId: String): UploadedMedia {
        val media: MediaObject = api.post("uploads/complete") {
            setBody(CompleteUploadRequest(uploadId = uploadId))
        }
        return UploadedMedia(
            id = media.id,
            url = media.url,
            mimeType = media.mimeType,
            sizeBytes = media.sizeBytes,
            width = media.width,
            height = media.height,
        )
    }
}

/**
 * The name sent to `createPresignedUpload`.
 *
 * Generated, never the one from the photo library -- which is why [PickedImage] has no filename
 * field for one to arrive in. DECISIONS §10 requires object keys that "must not contain email,
 * username, or original filename", and a filename is not inert metadata: it is a field people
 * put names, places and dates in. `PresignUploadRequest` documents that the server never stores
 * it, so this is belt and braces, and the belt costs nothing.
 */
// The prefix is the LOCAL enum's name, not the wire spelling: this string is a label for a
// human reading a log, not a contract value, and nothing should have to keep the two in step.
// A UUID plus an extension is 41 characters, so `maxLength: 255` is never in reach.
internal fun uploadFilename(
    purpose: UploadPurpose,
    mimeType: String,
    id: String = newUuid(),
): String = "${purpose.name.lowercase()}-$id.${extensionFor(mimeType)}"

/**
 * The contract's `mime_type` enum, as file extensions.
 *
 * The mapping itself lives in `:protocol` beside `ALLOWED_MIME_TYPES`, because the server needs
 * the same one to build object keys and the two must not drift. Only the fallback is local: an
 * unknown type is unreachable here (`upload` rejects anything outside the allowed set first), and
 * `bin` keeps this total rather than producing a filename ending in a bare dot.
 */
internal fun extensionFor(mimeType: String): String =
    UploadLimits.extensionFor(mimeType).ifEmpty { "bin" }
